import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { CATEGORIES, DIVISIONS } from "@/constants/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products & Solutions", dropdown: true },
  { to: "/certificates", label: "Downloads" },
  { to: "/contact", label: "Contact Us" },
];

export default function Navbar({ onQuoteClick }) {
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); setProductsOpen(false); }, [loc.pathname]);

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="site-navbar">
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-4" data-testid="logo-link">
          <div className="w-[68px] h-[68px] bg-[#B5121B] text-white flex items-center justify-center font-display font-black text-2xl rounded-sm shadow-sm flex-shrink-0">SSK</div>
          <div className="hidden sm:block leading-tight">
            <div className="font-display font-black text-[#B5121B] text-[26px] tracking-tight">SSK Enterprises</div>
            <div className="text-[11px] tracking-[0.10em] uppercase text-[#0F2747] font-semibold mt-0.5">Authorised Distributor of 3AK Chemie Pvt. Ltd.</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => n.dropdown ? (
            <div key={n.to} className="relative" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
              <button className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#0F2747] inline-flex items-center gap-1" data-testid="nav-products">
                {n.label} <ChevronDown className="w-4 h-4" />
              </button>
              {productsOpen && (
                <div className="absolute left-0 top-full pt-2 w-80" data-testid="products-dropdown">
                  <div className="bg-white border border-slate-200 shadow-xl py-2">
                    {Object.values(CATEGORIES).map((c) => (
                      <Link key={c.slug} to={`/products/${c.slug}`}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 border-l-2 border-transparent hover:border-l-[#0F2747]"
                        data-testid={`nav-category-${c.slug}`}>
                        <div className="w-1 h-8 bg-[#0F2747]" />
                        <div>
                          <div className="text-sm font-semibold text-[#1E293B]">{c.label}</div>
                          <div className="text-xs text-slate-500">{c.tagline}</div>
                        </div>
                      </Link>
                    ))}
                    <div className="border-t border-slate-100 my-1" />
                    <Link to="/products/bessey-clamps"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 border-l-2 border-transparent hover:border-l-[#B5121B]"
                      data-testid="nav-category-bessey-clamps">
                      <div className="w-1 h-8 bg-[#B5121B]" />
                      <div>
                        <div className="text-sm font-semibold text-[#1E293B]">BESSEY Clamps</div>
                        <div className="text-xs text-slate-500">Premium German clamping & cutting tools</div>
                      </div>
                    </Link>
                    <Link to="/products/industrial-bearings"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 border-l-2 border-transparent hover:border-l-[#B5121B]"
                      data-testid="nav-category-industrial-bearings">
                      <div className="w-1 h-8 bg-[#B5121B]" />
                      <div>
                        <div className="text-sm font-semibold text-[#1E293B]">Industrial Bearings</div>
                        <div className="text-xs text-slate-500">NTN & multi-brand — enquiry based</div>
                      </div>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <NavLink key={n.to} to={n.to} end={n.to === "/"}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium ${isActive ? "text-[#0F2747]" : "text-slate-700 hover:text-[#0F2747]"}`}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={onQuoteClick} className="hidden md:inline-flex btn-primary" data-testid="header-request-quote">
            Request a Quotation
          </button>
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="container-x py-4 flex flex-col gap-1">
            {NAV.filter(n => !n.dropdown).map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                className="py-2 text-slate-700 font-medium">{n.label}</NavLink>
            ))}
            <div className="pt-2 pb-1 text-[11px] font-mono-tech uppercase tracking-[0.18em] text-slate-500">Products</div>
            {Object.values(CATEGORIES).map((c) => (
              <Link key={c.slug} to={`/products/${c.slug}`} className="py-1.5 text-sm text-slate-600">{c.label}</Link>
            ))}
            <Link to="/products/bessey-clamps" className="py-1.5 text-sm text-slate-600">BESSEY Clamps</Link>
            <Link to="/products/industrial-bearings" className="py-1.5 text-sm text-slate-600">Industrial Bearings</Link>
            <button onClick={onQuoteClick} className="btn-primary mt-3" data-testid="mobile-request-quote">Request a Quotation</button>
          </div>
        </div>
      )}
    </header>
  );
}
